import React, { useMemo } from "react";
import { Tooltip } from "@panda0/panda0-ui";
import { cn } from "@panda0/panda0-utils";
import { motion } from "framer-motion";
import {
  Brain,
  Clock,
  Hammer,
  ThumbsDown,
  ThumbsUp,
  Trash,
} from "lucide-react";
import { useInView } from "react-intersection-observer";
import { z } from "zod";

import { PandaQuizQuestionSchema } from "@/lib/zod/schemas/job";

interface QuestionCardProps {
  question: z.infer<typeof PandaQuizQuestionSchema>;
  questionIndex: number;
  reactions: { [key: number]: string | null };
  handleReactionClick: (questionIndex: number, reaction: string) => void;
  handleRemoveQuestion: (questionIndex: number) => void;
  isMobile: boolean;
}

// Animation variants for cards
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 25 },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.2 },
  },
};

// Animation variants for options
const optionsContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const optionVariants = {
  hidden: { opacity: 0, y: 5 },
  visible: { opacity: 1, y: 0 },
};

// Question Card component with progressive rendering
const QuestionCard = React.memo(
  ({
    question,
    questionIndex,
    reactions,
    handleReactionClick,
    handleRemoveQuestion,
    isMobile,
  }: QuestionCardProps) => {
    const [ref, inView] = useInView({
      triggerOnce: true,
      threshold: 0.1,
      rootMargin: "100px 0px",
    });

    // Function to get color based on relevancy score
    const getRelevancyColor = (relevancy: number): string => {
      // Handle relevancy values between 0-1 (convert to 0-10 scale)
      const normalizedRelevancy =
        relevancy <= 1 ? Math.round(relevancy * 10) : relevancy;

      switch (normalizedRelevancy) {
        case 10:
          return "bg-green-500"; // Highest relevancy
        case 8:
          return "bg-blue-500"; // High relevancy
        case 6:
          return "bg-yellow-500"; // Medium relevancy
        case 4:
          return "bg-orange-500"; // Low relevancy
        default:
          return "bg-gray-400"; // Default for unexpected values
      }
    };

    // Function to get text for relevancy level
    const getRelevancyText = (relevancy: number): string => {
      // Handle relevancy values between 0-1 (convert to 0-10 scale)
      const normalizedRelevancy =
        relevancy <= 1 ? Math.round(relevancy * 10) : relevancy;

      switch (normalizedRelevancy) {
        case 10:
          return "Best Answer";
        case 8:
          return "Good Answer";
        case 6:
          return "Acceptable";
        case 4:
          return "Less Relevant";
        default:
          return "Not Relevant";
      }
    };

    // Pre-process question data to ensure consistent rendering
    const processedQuestion = useMemo(() => {
      return {
        ...question,
        skillTags: Array.isArray(question.skillTags) ? question.skillTags : [],
        taxonomyTags: Array.isArray(question.taxonomyTags)
          ? question.taxonomyTags
          : [],
      };
    }, [question]);

    return (
      <motion.div
        ref={ref}
        className="question-card rounded-xl border border-neutral-200 bg-white overflow-hidden"
        variants={cardVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        exit="exit"
        layout="position" // Use position-based layout to prevent dancing
      >
        {/* Question header */}
        <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-3">
          <div
            className={cn(
              "flex items-center justify-between",
              isMobile && "flex-col items-start gap-2"
            )}
          >
            <div
              className={cn(
                "flex items-center gap-4 flex-wrap",
                isMobile && "flex-wrap gap-2"
              )}
            >
              {/* Question number */}
              <div className="flex items-center justify-center h-6 w-6 rounded-full bg-black text-white text-sm font-semibold shrink-0">
                {questionIndex + 1}
              </div>

              {/* Skills - only show if available */}
              {processedQuestion.skillName && (
                <div className="flex items-center gap-1.5">
                  <Hammer className="h-4 w-4 text-neutral-500" />
                  <span className="text-sm font-medium">Skills:</span>
                  <span className="text-xs bg-blue-50 text-green-700 px-2 py-0.5 rounded-full">
                    {processedQuestion.skillName}
                  </span>
                </div>
              )}

              {/* Experience level */}
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-neutral-500" />
                <span className="text-sm font-medium">Experience:</span>
                <span className="text-xs bg-blue-50 text-green-700 px-2 py-0.5 rounded-full">
                  {processedQuestion.experienceLevel}
                </span>
              </div>

              {/* Skill Tags - only if they exist and not empty */}
              {processedQuestion.skillTags.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {processedQuestion.skillTags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="text-xs bg-blue-50 text-green-700 px-2 py-0.5 rounded-full"
                    >
                      {typeof tag === "string" ? tag : tag.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Taxonomy Tags - only if they exist and not empty */}
              {processedQuestion.taxonomyTags.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Brain className="h-4 w-4 text-neutral-500" />
                  <span className="text-sm font-medium">Taxonomy:</span>
                  {processedQuestion.taxonomyTags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="text-xs bg-blue-50 text-green-700 px-2 py-0.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="action-buttons flex items-center gap-1 shrink-0">
              {/* Like/dislike buttons */}
              <div className="flex gap-1">
                <Tooltip content="Like question">
                  <button
                    onClick={() =>
                      handleReactionClick(questionIndex, "thumbs-up")
                    }
                    className={cn(
                      "p-1.5 rounded-md transition-colors",
                      reactions[questionIndex] === "thumbs-up"
                        ? "text-green-500 bg-green-50"
                        : "text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100"
                    )}
                  >
                    <ThumbsUp className="h-4 w-4" />
                  </button>
                </Tooltip>

                <Tooltip content="Dislike question">
                  <button
                    onClick={() =>
                      handleReactionClick(questionIndex, "thumbs-down")
                    }
                    className={cn(
                      "p-1.5 rounded-md transition-colors",
                      reactions[questionIndex] === "thumbs-down"
                        ? "text-red-500 bg-red-50"
                        : "text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100"
                    )}
                  >
                    <ThumbsDown className="h-4 w-4" />
                  </button>
                </Tooltip>
              </div>

              <Tooltip content="Remove question">
                <button
                  onClick={() => handleRemoveQuestion(questionIndex)}
                  className="p-1.5 text-red-500 hover:text-red-700 rounded-md hover:bg-red-50"
                >
                  <Trash className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Question content */}
        <div className="p-4">
          <div className="space-y-4">
            <p className="text-neutral-800 font-medium">
              {processedQuestion.question}
            </p>

            {/* Options grid */}
            <motion.div
              className={cn(
                "grid gap-3",
                isMobile ? "grid-cols-1" : "grid-cols-2"
              )}
              variants={optionsContainerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              {processedQuestion.options &&
                processedQuestion.options.map((option, index: number) => {
                  // Normalize relevancy score for consistency
                  const rawRelevancy = option.relevancy || 0;
                  const relevancyScore =
                    rawRelevancy <= 1
                      ? Math.round(rawRelevancy * 10)
                      : rawRelevancy;

                  const relevancyColor = getRelevancyColor(relevancyScore);
                  const relevancyText = getRelevancyText(relevancyScore);

                  // Pre-process option skill tags
                  const optionSkillTags = Array.isArray(option.skillTags)
                    ? option.skillTags
                    : [];

                  return (
                    <motion.div
                      key={index}
                      variants={optionVariants}
                      className={cn(
                        "flex flex-col p-3 border rounded-lg",
                        relevancyScore === 10
                          ? "border-green-500 bg-green-50"
                          : relevancyScore === 8
                            ? "border-blue-500 bg-blue-50"
                            : relevancyScore === 6
                              ? "border-yellow-500 bg-yellow-50"
                              : "border-orange-500 bg-orange-50"
                      )}
                    >
                      <div className="flex items-start">
                        <div
                          className={cn(
                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border mr-3 text-sm",
                            relevancyScore === 10
                              ? "border-green-500 bg-green-500 text-white"
                              : relevancyScore === 8
                                ? "border-blue-500 bg-blue-500 text-white"
                                : relevancyScore === 6
                                  ? "border-yellow-500 bg-yellow-500 text-white"
                                  : "border-orange-500 bg-orange-500 text-white"
                          )}
                        >
                          {String.fromCharCode(65 + index)}
                        </div>

                        <div className="text-sm flex-1">
                          <p>{option.option}</p>

                          {/* Show experience range */}
                          <div className="mt-2 flex flex-wrap items-center gap-1">
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                              {(() => {
                                const min = option.experienceRange?.min ?? 0;
                                const max = option.experienceRange?.max ?? 0;

                                // Sort values to ensure smaller value comes first
                                const [smaller, larger] = [min, max].sort(
                                  (a, b) => a - b
                                );

                                if (smaller === larger) {
                                  if (smaller === 0) {
                                    return `Experience: Fresher`;
                                  } else if (smaller === 1) {
                                    return `Experience: 1 year`;
                                  } else {
                                    return `Experience: ${smaller} years`;
                                  }
                                } else {
                                  // Range case
                                  return `Experience: ${smaller}-${larger} years`;
                                }
                              })()}
                            </span>

                            {/* Display skill tags inline with experience */}
                            {optionSkillTags.length > 0 && (
                              <>
                                <span className="text-xs text-neutral-500 mx-1">
                                  •
                                </span>
                                <div className="flex items-center flex-wrap gap-1">
                                  {optionSkillTags.map((tag, tagIndex) => (
                                    <span
                                      key={tagIndex}
                                      className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
                                    >
                                      {typeof tag === "string" ? tag : tag.name}
                                    </span>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Relevancy and score indicators */}
                      <div className="mt-3 w-full">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">
                            {relevancyText}
                          </span>
                          <span className="text-xs font-medium flex items-center gap-1">
                            Relevancy:
                            <span
                              className={cn(
                                "inline-flex items-center justify-center h-5 w-5 rounded-full text-white text-xs font-medium",
                                relevancyScore === 10
                                  ? "bg-green-500"
                                  : relevancyScore === 8
                                    ? "bg-blue-500"
                                    : relevancyScore === 6
                                      ? "bg-yellow-500"
                                      : "bg-orange-500"
                              )}
                            >
                              {relevancyScore}
                            </span>
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden w-full">
                          <div
                            className={`h-full ${relevancyColor}`}
                            style={{ width: `${relevancyScore * 10}%` }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }
);

QuestionCard.displayName = "QuestionCard";

export default React.memo(QuestionCard);
